import { v4 as uuidV4 } from "uuid";

import { Appointment } from "services/types/appointments.type";
import { Clinic } from "services/types/clinics.type";
import { ClinicEvent, Events, PersonalEvent } from "services/types/events.type";
import { MinimalStaffMember } from "services/types/staff.type";
import { convertDateInTimezoneToCurrent } from "utils/common";

export const getEventsData = (
  events: Events | undefined,
  skip: boolean,
  selectedClinics: Clinic[] = [],
  showByClinic: boolean = false,
  defaultClinic: Clinic,
  selectedStaff: MinimalStaffMember[] = [],
  includeSupervisingProvider: boolean = false,
  allClinics: Clinic[] = []
) => {
  if (skip)
    return {
      appointments: [],
      personalEvents: [],
      appointmentBlocks: [],
      personalWorkShift: [],
      workShift: undefined
    };

  const {
    appointments,
    personalEvents,
    appointmentBlocks,
    personalWorkShift,
    workShift
  } = events ?? {
    appointments: [],
    personalEvents: [],
    appointmentBlocks: [],
    personalWorkShift: [],
    workShift: undefined
  };

  const normalizedPersonalEvents = personalEvents.map(
    (event: PersonalEvent | ClinicEvent) => {
      return {
        ...event,
        id: [
          event.id,
          event.staffMemberId,
          event.clinicId,
          event.start_moment
        ].join("-")
      };
    }
  );

  // Process supervising provider logic for appointments
  const processedAppointments: any[] = [];
  const selectedStaffIds = new Set(selectedStaff.map((staff) => staff.id));
  const processedAppointmentIds = new Set(); // Track which appointments we've already processed

  appointments.forEach((appointment: Appointment) => {
    if (appointment.supervising_provider && includeSupervisingProvider) {
      const supervisingProviderId = appointment.supervising_provider.id;
      const appointmentStaffId = appointment.staffMemberId;

      const supervisingProviderInSelected = selectedStaffIds.has(
        supervisingProviderId
      );
      const appointmentStaffInSelected =
        selectedStaffIds.has(appointmentStaffId);

      if (supervisingProviderInSelected && appointmentStaffInSelected) {
        // Both staff and supervising provider are selected
        // We get two events from API, modify only one to show under supervising provider
        if (!processedAppointmentIds.has(appointment.id)) {
          // First occurrence - keep as original staff appointment
          processedAppointments.push(appointment);
          processedAppointmentIds.add(appointment.id);
        } else {
          // Second occurrence - modify this one to show under supervising provider
          const supervisingProviderAppointment = {
            ...appointment,
            id: `${appointment.id}-supervising-${supervisingProviderId}`,
            modelId: appointment.id,
            staffMemberId: supervisingProviderId
          };
          processedAppointments.push(supervisingProviderAppointment);
        }
      } else if (supervisingProviderInSelected && !appointmentStaffInSelected) {
        // Only supervising provider is selected - show in supervising provider column
        const supervisingProviderAppointment = {
          ...appointment,
          id: `${appointment.id}-supervising-${supervisingProviderId}`,
          modelId: appointment.id,
          staffMemberId: supervisingProviderId
        };
        processedAppointments.push(supervisingProviderAppointment);
      } else if (appointmentStaffInSelected) {
        // Only appointment staff is selected - show in staff column (normal case)
        processedAppointments.push(appointment);
      }
      // If neither is selected, don't show the appointment
    } else {
      // No supervising provider OR include supervising provider is disabled - show if staff is selected
      if (selectedStaffIds.has(appointment.staffMemberId)) {
        processedAppointments.push(appointment);
      }
    }
  });

  if (!showByClinic) {
    const clinicTimeZone = defaultClinic.timezone;
    const finalPersonalEvents = normalizedPersonalEvents.map(
      (event: PersonalEvent | ClinicEvent) => {
        if (event.isGlobalPracticeEvent) {
          return {
            ...event,
            id: [
              event.id,
              event.staffMemberId,
              defaultClinic.id,
              event.start_moment
            ].join("-"),
            clinic: defaultClinic,
            clinicId: defaultClinic.id,
            startDate: event.start_moment
              ? convertDateInTimezoneToCurrent(
                  event.start_moment,
                  clinicTimeZone
                ).toISOString()
              : event.startDate,
            endDate: event.end_moment
              ? convertDateInTimezoneToCurrent(
                  event.end_moment,
                  clinicTimeZone
                ).toISOString()
              : event.endDate
          };
        }
        return event;
      }
    );

    return {
      appointments: processedAppointments,
      personalEvents: finalPersonalEvents,
      appointmentBlocks,
      personalWorkShift,
      workShift
    };
  }

  const availableClinics =
    allClinics.length > 0
      ? allClinics
      : Array.from(
          new Map(
            [...appointments, ...personalEvents, ...appointmentBlocks].map(
              ({ clinic }) => [clinic.id, clinic]
            )
          ).values()
        );
  const clinics =
    selectedClinics.length > 0 ? selectedClinics : availableClinics;
  const personalEventsGlobal = normalizedPersonalEvents.filter(
    (event) => event.isGlobalPracticeEvent
  );
  const personalEventsLocal = normalizedPersonalEvents.filter(
    (event) => !event.isGlobalPracticeEvent
  );

  const finalPersonalEvents = personalEventsGlobal.reduce(
    (
      acc: (PersonalEvent | ClinicEvent)[],
      event: PersonalEvent | ClinicEvent
    ) => {
      clinics.forEach((clinic) => {
        const clinicTimeZone = clinic?.timezone;
        const personalEvent = {
          ...event,
          id: uuidV4(),
          clinicId: clinic.id,
          clinic: clinic,
          startDate: event.start_moment
            ? convertDateInTimezoneToCurrent(
                event.start_moment,
                clinicTimeZone
              ).toISOString()
            : event.startDate,
          endDate: event.end_moment
            ? convertDateInTimezoneToCurrent(
                event.end_moment,
                clinicTimeZone
              ).toISOString()
            : event.endDate
        };
        acc.push(personalEvent);
      });
      return acc;
    },
    [...personalEventsLocal]
  );

  return {
    appointments: processedAppointments,
    personalEvents: finalPersonalEvents,
    appointmentBlocks,
    personalWorkShift,
    workShift
  };
};
